'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ChevronRight,
  FolderPlus,
  Images,
  Loader2,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import {
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  SIDEBAR_ACTION_BUTTON_CLASS,
  SIDEBAR_ICON_CLASS,
  SIDEBAR_SECTION_HEADER_ROW_CLASS,
} from './sidebar-row-metrics';
import { SIDEBAR_SECTION_LABEL_CLASS } from './sidebar-section-label';

type ProjectsToolbarProps = {
  isProjectsSectionExpanded: boolean;
  onToggleProjectsSection: () => void;
  allProjectsExpanded: boolean;
  onToggleAllProjects: () => void;
  /** Omitted on surfaces without a project search (e.g. the /threads page). */
  onOpenProjectSearch?: () => void;
  onOpenOrganizer: () => void;
  onCreateProject: () => void;
  /** Projects the collapsed heading counts ("+" while more pages remain). */
  projectsCount?: number;
  projectsCountIsPartial?: boolean;
};

/** Native button props a wrapping trigger (e.g. a Radix `asChild` menu
 * trigger) passes through; motion-owned handlers are excluded. */
type HoverLabelPassThroughProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  | 'children'
  | 'onAnimationStart'
  | 'onDrag'
  | 'onDragEnd'
  | 'onDragStart'
  | 'onClick'
  | 'disabled'
>;

/**
 * Collapsed width, label inset and the extra width the label needs, per size.
 * `compact` (24px, 14px glyph) serves the chat surface; `sidebar` is the
 * panel's 32px action button around a 20px glyph (see sidebar-row-metrics).
 */
const HOVER_LABEL_SIZES = {
  compact: {
    width: 24,
    labelInset: 36,
    className: 'h-6 w-6 min-w-6 rounded-md',
    labelClassName: 'left-7',
  },
  sidebar: {
    width: 32,
    labelInset: 40,
    className: cn(SIDEBAR_ACTION_BUTTON_CLASS, 'min-w-8'),
    labelClassName: 'left-8',
  },
} as const;

export function HoverLabelIconButton({
  label,
  ariaLabel,
  onClick,
  disabled = false,
  labelClassName,
  size = 'compact',
  children,
  ref,
  onFocus,
  onBlur,
  ...passThrough
}: {
  label: string;
  ariaLabel: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  labelClassName?: string;
  size?: keyof typeof HOVER_LABEL_SIZES;
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
} & HoverLabelPassThroughProps) {
  const metrics = HOVER_LABEL_SIZES[size];
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedWidth, setExpandedWidth] = useState<number>(metrics.width);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) return;
    setExpandedWidth(
      Math.max(
        metrics.width,
        metrics.labelInset + Math.ceil(labelElement.offsetWidth)
      )
    );
  }, [label, metrics]);

  return (
    <Button
      asChild
      size="icon"
      variant="ghost"
      className={cn(
        'text-muted-foreground hover:bg-primary/5 hover:text-foreground relative justify-start overflow-hidden px-1.5',
        metrics.className
      )}
    >
      <motion.button
        {...passThrough}
        ref={(node: HTMLButtonElement | null) => {
          buttonRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="button"
        onClick={onClick}
        disabled={disabled}
        onHoverStart={() => setIsExpanded(true)}
        onHoverEnd={() => setIsExpanded(false)}
        onFocus={(event) => {
          setIsExpanded(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setIsExpanded(false);
          onBlur?.(event);
        }}
        aria-label={ariaLabel}
        initial={false}
        animate={{ width: isExpanded ? expandedWidth : metrics.width }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: 'spring', duration: 0.35, bounce: 0 }
        }
      >
        {children}
        <motion.span
          ref={labelRef}
          initial={false}
          animate={{ opacity: isExpanded ? 1 : 0, x: isExpanded ? 0 : -4 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.2, ease: 'easeOut' }
          }
          data-hover-label
          aria-hidden={!isExpanded}
          className={cn(
            'text-muted-foreground pointer-events-none absolute text-xs whitespace-nowrap',
            metrics.labelClassName,
            labelClassName
          )}
        >
          {label}
        </motion.span>
      </motion.button>
    </Button>
  );
}

export function ProjectsToolbar({
  isProjectsSectionExpanded,
  onToggleProjectsSection,
  allProjectsExpanded: _allProjectsExpanded,
  onToggleAllProjects: _onToggleAllProjects,
  onOpenProjectSearch,
  onOpenOrganizer,
  onCreateProject,
  projectsCount,
  projectsCountIsPartial,
}: ProjectsToolbarProps) {
  const t = useTranslations('WorkspaceShell');

  return (
    <div
      data-sidebar-section="projects"
      className={cn(
        'group/projects-toolbar justify-between',
        SIDEBAR_SECTION_HEADER_ROW_CLASS
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-0.5">
        <button
          type="button"
          onClick={onToggleProjectsSection}
          aria-expanded={isProjectsSectionExpanded}
          aria-label={
            isProjectsSectionExpanded
              ? t('collapseSection', { title: t('projects') })
              : t('expandSection', { title: t('projects') })
          }
          title={
            isProjectsSectionExpanded
              ? t('collapseSection', { title: t('projects') })
              : t('expandSection', { title: t('projects') })
          }
          className={cn(
            'hover:text-foreground text-sidebar-section-foreground group/projects-heading flex w-full min-w-0 cursor-pointer items-center gap-1 rounded py-0.5 pr-1 text-left transition-colors',
            SIDEBAR_SECTION_LABEL_CLASS
          )}
        >
          <span className="truncate">{t('projects')}</span>
          {isProjectsSectionExpanded ? null : (
            <CollapsedSectionCount
              count={projectsCount}
              partial={projectsCountIsPartial}
            />
          )}
          <ChevronRight
            className={cn(
              'size-3 shrink-0 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/projects-heading:opacity-100 group-hover/projects-heading:opacity-100',
              isProjectsSectionExpanded && 'rotate-90'
            )}
          />
        </button>
      </div>
      <div className="flex items-center gap-1">
        {/* Temporarily hidden: expand/collapse all projects control.
        <Button
          size="icon"
          variant="ghost"
          className="text-muted-foreground hover:bg-primary/5 hover:text-foreground h-6 w-6 rounded-md"
          onClick={onToggleAllProjects}
          disabled={!isProjectsSectionExpanded}
          aria-label={allProjectsExpanded ? t('collapseAll') : t('expandAll')}
          title={allProjectsExpanded ? t('collapseAll') : t('expandAll')}
        >
          {allProjectsExpanded ? (
            <Minimize2 className="!size-3" />
          ) : (
            <Maximize2 className="!size-3" />
          )}
        </Button>
        */}
        <HoverLabelIconButton
          size="sidebar"
          label={t('organize')}
          onClick={onOpenOrganizer}
          ariaLabel={t('organizeProjects')}
        >
          <SlidersHorizontal
            className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)}
          />
        </HoverLabelIconButton>
        <HoverLabelIconButton
          size="sidebar"
          label={t('newProject')}
          onClick={onCreateProject}
          ariaLabel={t('newProject')}
        >
          <FolderPlus
            className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)}
          />
        </HoverLabelIconButton>
        {onOpenProjectSearch ? (
          <HoverLabelIconButton
            size="sidebar"
            label={t('search')}
            onClick={onOpenProjectSearch}
            ariaLabel={t('searchProjects')}
          >
            <Search
              className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)}
            />
          </HoverLabelIconButton>
        ) : null}
      </div>
    </div>
  );
}

/** Chat-list tools (media library, search) that expand on hover. */
export function ChatTools({
  onOpenSearch,
  onOpenLibrary,
  className,
}: {
  onOpenSearch?: () => void;
  onOpenLibrary?: () => void;
  className?: string;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {onOpenLibrary && (
        <HoverLabelIconButton
          size="sidebar"
          label={t('myLibrary')}
          onClick={onOpenLibrary}
          ariaLabel={t('openLibrary')}
        >
          <Images className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)} />
        </HoverLabelIconButton>
      )}
      {onOpenSearch && (
        <HoverLabelIconButton
          size="sidebar"
          label={t('search')}
          onClick={onOpenSearch}
          ariaLabel={t('searchChats')}
        >
          <Search className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)} />
        </HoverLabelIconButton>
      )}
    </div>
  );
}

export function ChatsSectionHeader({
  onOpenSearch,
  onOpenLibrary,
  isExpanded,
  onToggle,
}: {
  onOpenSearch?: () => void;
  onOpenLibrary?: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div
      data-sidebar-section="chats"
      className={cn(
        'group/chats-toolbar justify-between gap-1.5',
        SIDEBAR_SECTION_HEADER_ROW_CLASS
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={cn(
          'text-sidebar-section-foreground hover:text-foreground group/chats-heading flex flex-1 items-center gap-1 text-left',
          SIDEBAR_SECTION_LABEL_CLASS
        )}
      >
        {t('recentChats')}
        <ChevronRight
          className={cn(
            'size-3 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/chats-heading:opacity-100 group-hover/chats-heading:opacity-100',
            isExpanded && 'rotate-90'
          )}
        />
      </button>
      <ChatTools onOpenSearch={onOpenSearch} onOpenLibrary={onOpenLibrary} />
    </div>
  );
}

/** How many items a collapsed section hides, beside its heading. */
function CollapsedSectionCount({
  count,
  partial = false,
}: {
  count?: number;
  partial?: boolean;
}) {
  if (!count) return null;
  return (
    <span
      data-sidebar-section-count
      className="text-muted-foreground/60 ms-0.5 text-xs font-medium normal-case tabular-nums"
    >
      {count}
      {partial ? '+' : null}
    </span>
  );
}

export function FavoritesSectionHeader({
  isPending,
  isExpanded,
  onToggle,
  count,
}: {
  isPending?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /** Pinned items, shown while the section is collapsed. */
  count?: number;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div
      data-sidebar-section="pinned"
      className={cn(
        'justify-between gap-1.5',
        SIDEBAR_SECTION_HEADER_ROW_CLASS
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={cn(
          'text-sidebar-section-foreground hover:text-foreground group/pinned-heading flex flex-1 items-center gap-1 text-left',
          SIDEBAR_SECTION_LABEL_CLASS
        )}
      >
        {t('favorites')}
        {isExpanded ? null : <CollapsedSectionCount count={count} />}
        <ChevronRight
          className={cn(
            'size-3 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/pinned-heading:opacity-100 group-hover/pinned-heading:opacity-100',
            isExpanded && 'rotate-90'
          )}
        />
      </button>
      {isPending && (
        <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
      )}
    </div>
  );
}

// type RootThreadsListProps
